<template>
  <div class="ranking-table">
    <h4>{{ $t('rounds.table') }}</h4>

    <div v-if="showExpiredModal" class="modal-overlay">
      <div class="modal-card">
        <h5 class="mb-2">{{ $t('competitions.messages.upload_time_expired') || 'El tiempo de subida ha terminado' }}</h5>
        <p class="small text-muted mb-3">{{ $t('competitions.messages.upload_time_expired_desc') || 'Lo sentimos, la ventana de subida ya se cerró.' }}</p>
        <div class="d-flex justify-content-end">
          <button class="btn btn-sm btn-primary" type="button" @click="closeExpiredModal">{{ $t('common.ok') || 'Entendido' }}</button>
        </div>
      </div>
    </div>

    <UploadModal
      :visible="showUploadModal"
      :athlete="uploadModalAthlete"
      :round-label="roundLabel(uploadModalAthlete)"
      :code="getCode(uploadModalAthlete)"
      :meta="uploadModalMeta"
      :issues="uploadModalIssues"
      :error="uploadModalError"
      :progress-percent="currentUploadRoundId && uploadState[currentUploadRoundId]?.percent !== undefined ? modalProgressPercent : null"
      :progress-chunks="modalProgressChunks"
      :is-uploading="isModalUploading"
      :is-completed="isModalCompleted"
      :can-retry="modalUploadState?.status === 'error' && !!uploadModalFile"
      :disable-confirm="!uploadModalFile || isModalUploading || !!(uploadModalIssues && uploadModalIssues.length)"
      @close="closeUploadModal"
      @confirm="confirmUpload"
      @retry="retryUpload"
      @file-change="onUploadFileChange"
    />

    <div class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>{{ $t('rounds.name') }}</th>
            <th></th>
            <th>{{ $t('rounds.status') }}</th>
            <th v-if="canShowScoreSort" @click="toggleScoreSort" style="cursor: pointer;">
              {{ $t('rounds.score') }}
              <span v-if="scoreSortDirection === 'asc'">↑</span>
              <span v-else-if="scoreSortDirection === 'desc'">↓</span>
              <span v-else>⇅</span>
            </th>
            <th v-else>
              {{ $t('rounds.score') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(athlete, index) in paginatedRounds"
            :key="athlete.id"
            :class="[
              { 'qualifier-row': isAdvancingAthlete(athlete) },
              { 'highlighted-row': updatedRowIds.has(athlete.id) },
              { 'clickable-row': isClickable(athlete) }
            ]"
            @click.prevent="handleRoundClick(athlete)"
          >
            <td class="athlete-header-cell" :data-label="'#'">
              <div class="athlete-header">
                <span class="number">{{ pageStartIndex + index + 1 }}</span>
                <img :src="athlete.img || defaultAvatar" class="athlete-img" alt="" />
              </div>
            </td>

            <td :data-label="$t('rounds.name')" class="athlete-name">
              {{ athlete.name }} {{ athlete.surname }}
            </td>

            <td class="round-warning-col" data-label="">
              <template v-if="isOnlineCompetition && canShowVideoActions(athlete)">
                <button
                  class="btn btn-sm btn-primary video-button"
                  :class="{ 'video-button--blocked': uploadButtonConfig(athlete).expired }"
                  :disabled="false"
                  @click.stop="onUploadButtonClick(athlete)"
                  aria-label="Subir video"
                  title="Subir video"
                >
                  <i class="fas fa-cloud-upload-alt"></i>
                </button>
              </template>
              <template v-else>
                <span v-if="needsAlert(athlete)" class="message-alert">
                  {{ $t('judge.message-alert-short') }}
                </span>
                <div v-else-if="isOwner(athlete)" class="check-message">
                  <span class="message-check" v-if="isValid(athlete)">
                    <span class="check-wrapper check-wrapper--double">
                      <i class="fas fa-check" style="color: #007bff;"></i>
                      <i class="fas fa-check" style="color: #007bff; margin-left: -6px;"></i>
                    </span>
                  </span>
                  <span class="message-check" v-if="!isValid(athlete)">
                    <span class="check-wrapper check-wrapper--single">
                      <i class="fas fa-check"></i>
                      <i class="fas fa-check" style="opacity: 0.3; margin-left: -6px;"></i>
                    </span>
                  </span>
                </div>
                <div v-if="isInjured(athlete)" class="status-message">
                  <span class="message-injured">
                    <span class="icon-wrapper">
                      <i class="fas fa-user-injured" title="Lastimado" style="color: #dc3545;"></i>
                    </span>
                  </span>
                </div>
                <div v-else-if="isEliminated(athlete)" class="status-message">
                  <span class="message-eliminated">
                    <span class="icon-wrapper">
                      <i class="fas fa-user-slash" style="color: #6c757d;"></i>
                    </span>
                  </span>
                </div>
              </template>
            </td>

            <td :data-label="$t('rounds.status')">
              <span :class="statusClass(athlete.status_id)">
                {{ getStatusLabel(athlete.status_id) }}
              </span>
            </td>

            <td :data-label="$t('rounds.score')" class="score">
              <span v-if="canViewScore(athlete)">{{ formatScore(athlete.total_score) }}</span>
              <span v-else>–</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="totalPages > 1" class="table-pagination" role="navigation" :aria-label="$t('competitions.pagination.page') || 'Pagination'">
      <button
        type="button"
        class="btn btn-sm btn-outline-secondary"
        :disabled="currentPage === 1"
        @click="goToPreviousPage"
      >
        {{ $t('competitions.pagination.prev') || 'Previous' }}
      </button>

      <span class="table-pagination-status">
        {{ $t('competitions.pagination.page') || 'Page' }} {{ currentPage }} {{ $t('competitions.pagination.of') || 'of' }} {{ totalPages }}
      </span>

      <button
        type="button"
        class="btn btn-sm btn-outline-secondary"
        :disabled="currentPage >= totalPages"
        @click="goToNextPage"
      >
        {{ $t('competitions.pagination.next') || 'Next' }}
      </button>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { isInnerAthlete, isOwnerJudgeFromCompetition, isOwnerOrganizer } from '@/helpers/roleUtils';
import UploadModal from '@/components/competitions/UploadModal.vue';

export default {
  name: 'CompetitionTable',
  components: { UploadModal },
  props: {
    competition: {
      type: Object,
      required: false,
      default: null
    },
    rounds: {
      type: Array,
      required: true
    },
    advancingCount: {
      type: Number,
      required: false,
      default: 0
    },
    selectedStage: {
      type: [Number, null],
      required: false,
      default: null
    },
    selectedRound: {
      type: [String, Number, null],
      required: false,
      default: null
    }
  },
  data() {
    return {
      defaultAvatar: require('@/assets/user.png'),
      updatedRowIds: new Set(),
      scoreSortDirection: null,
      currentPage: 1,
      pageSize: 16,
      uploadState: {},
      nowTs: Date.now(),
      showUploadModal: false,
      showExpiredModal: false,
      uploadModalAthlete: null,
      uploadModalFile: null,
      uploadModalError: '',
      uploadModalIssues: [],
      uploadModalMeta: null,
      uploadFiles: {},
      tricklistVersions: [],
      pendingRoundOverrideIds: new Set(),
    };
  },
  created() {
    this.tickInterval = setInterval(() => {
      this.nowTs = Date.now();
    }, 60000);
    this.syncUploadStatesFromRounds(this.rounds);
    this.fetchTricklistVersions();
  },
  beforeUnmount() {
    if (this.tickInterval) {
      clearInterval(this.tickInterval);
    }
  },
  computed: {
    ...mapState('user', ['user', 'token']),
    ...mapState('competition', ['competitionIdToView', 'competitionData']),
    currentCompetition() {
      return this.competition || this.competitionData || {};
    },
    authToken() {
      const direct = this.token;
      if (direct) return direct;
      const stored = (localStorage.getItem('token') || '').replace(/"/g, '');
      return stored || '';
    },
    authHeader() {
      return this.authToken ? { Authorization: `Bearer ${this.authToken}` } : {};
    },
    canShowScoreSort() {
      if (!(this.isOwnerJudgeFromCompetition(this.user, this.competitionData) ||
          this.isOwnerOrganizer(this.user, this.competitionData))) {
        return false;
      }
      return this.rounds.some(r => r.status_id !== 10);
    },
    canManageRoundTricklists() {
      return this.isOwnerOrganizer(this.user, this.currentCompetition);
    },
    competitionDefaultTricklistLabel() {
      const defaultName = this.currentCompetition?.tricklist_version?.name || 'Competition default';
      return `Competition default: ${defaultName}`;
    },
    isVsCompetition() {
      return Boolean(Number(this.currentCompetition?.is_vs));
    },
    isOnlineCompetition() {
      return Number(this.currentCompetition?.type_id) === 1;
    },
    stageCalendars() {
      const calendars = this.currentCompetition?.stage_calendars || this.currentCompetition?.stageCalendars;
      return Array.isArray(calendars) ? calendars : [];
    },
    stageCalendarsById() {
      const map = {};
      this.stageCalendars.forEach(c => {
        const key = c?.round_size_id ?? c?.id;
        if (key !== undefined && key !== null) map[Number(key)] = c;
      });
      return map;
    },
    stageCalendarsByCode() {
      const map = {};
      this.stageCalendars.forEach(c => {
        if (c?.stage_code) map[c.stage_code] = c;
      });
      return map;
    },
    roundSizes() {
      return Array.isArray(this.currentCompetition?.round_sizes) ? this.currentCompetition.round_sizes : [];
    },
    roundSizesById() {
      const map = {};
      this.roundSizes.forEach(rs => {
        if (rs && rs.id !== undefined && rs.id !== null) map[Number(rs.id)] = rs;
      });
      return map;
    },
    currentUploadRoundId() {
      return this.uploadModalAthlete?.round_id || null;
    },
    modalUploadState() {
      const rid = this.currentUploadRoundId;
      return rid ? (this.uploadState[rid] || {}) : {};
    },
    isModalUploading() {
      return this.modalUploadState?.status === 'uploading';
    },
    isModalCompleted() {
      return this.modalUploadState?.status === 'completed';
    },
    modalProgressPercent() {
      const pct = Number(this.modalUploadState?.percent || 0);
      return Math.min(100, Math.max(0, Math.round(pct)));
    },
    modalProgressChunks() {
      const done = Number(this.modalUploadState?.nextChunk || 0);
      const total = Number(this.modalUploadState?.totalChunks || 0);
      if (!total) return `${done}/?`;
      return `${Math.min(done, total)}/${total}`;
    },
    sortedRounds() {
      const list = Array.isArray(this.rounds) ? this.rounds : [];
      if (!this.scoreSortDirection) return list;
      const dir = this.scoreSortDirection === 'asc' ? 1 : -1;
      return [...list].sort((a, b) => {
        const aScore = parseFloat(a.total_score) || 0;
        const bScore = parseFloat(b.total_score) || 0;
        return dir * (aScore - bScore);
      });
    },
    totalPages() {
      const totalItems = this.sortedRounds.length;
      return Math.max(1, Math.ceil(totalItems / this.pageSize));
    },
    pageStartIndex() {
      return (this.currentPage - 1) * this.pageSize;
    },
    paginatedRounds() {
      return this.sortedRounds.slice(this.pageStartIndex, this.pageStartIndex + this.pageSize);
    },
    advancingAthleteIds() {
      if (!this.isAdvancingContext() || !this.isAdvancingContextResolved()) {
        return new Set();
      }

      const qualifierCount = this.resolveAdvancerCount();
      if (!qualifierCount) {
        return new Set();
      }

      const rankedPublished = (Array.isArray(this.rounds) ? this.rounds : [])
        .filter(athlete => Number(athlete?.status_id) === 10 && !athlete?.disqualified_at)
        .sort((left, right) => {
          const rightScore = parseFloat(right?.total_score) || 0;
          const leftScore = parseFloat(left?.total_score) || 0;
          if (rightScore !== leftScore) {
            return rightScore - leftScore;
          }

          const leftSurname = String(left?.surname || '');
          const rightSurname = String(right?.surname || '');
          const surnameCompare = leftSurname.localeCompare(rightSurname);
          if (surnameCompare !== 0) {
            return surnameCompare;
          }

          return String(left?.name || '').localeCompare(String(right?.name || ''));
        });

      return new Set(
        rankedPublished
          .slice(0, qualifierCount)
          .map(athlete => athlete?.id)
          .filter(id => id !== null && id !== undefined)
      );
    }
  },
  watch: {
    rounds(newRounds, oldRounds) {
      this.syncUploadStatesFromRounds(newRounds);
      this.ensureValidPage();
      if (!oldRounds) return;
      const byId = new Map((oldRounds || []).map(r => [r.id, r]));
      newRounds.forEach(newItem => {
        const oldItem = byId.get(newItem.id);
        if (oldItem && newItem.status_id !== oldItem.status_id) {
          this.updatedRowIds.add(newItem.id);
          setTimeout(() => {
            this.updatedRowIds.delete(newItem.id);
            this.updatedRowIds = new Set(this.updatedRowIds);
          }, 2000);
        }
      });
    },
    selectedRound() {
      this.currentPage = 1;
    },
    selectedStage() {
      this.currentPage = 1;
    },
    scoreSortDirection() {
      this.currentPage = 1;
    }
  },
  methods: {
    isOwnerOrganizer,
    isOwnerJudgeFromCompetition,
    isInnerAthlete,
    async fetchTricklistVersions() {
      if (!this.authToken || !this.canManageRoundTricklists) {
        return;
      }

      try {
        const response = await this.$axios.get('/competitions', { headers: this.authHeader });
        const rawVersions = Array.isArray(response.data?.tricklist_versions) ? response.data.tricklist_versions : [];
        this.tricklistVersions = rawVersions.map((version) => ({
          value: version.id,
          name: version.name || `${version.year}`,
        }));
      } catch (error) {
        console.error('Error loading tricklist versions:', error);
      }
    },
    canViewScore(athlete) {
      if (!athlete) return false;

      const isSelf = this.isInnerAthlete(this.user, athlete);
      const isPublished = Number(athlete.status_id) === 10;

      if (isSelf && !isPublished) return false;

      return isPublished || this.isOwnerOrganizer(this.user, this.competitionData) || this.isOwnerJudgeFromCompetition(this.user, this.competitionData);
    },
    syncUploadStatesFromRounds(rounds) {
      const list = Array.isArray(rounds) ? rounds : this.rounds;
      if (!Array.isArray(list)) return;
      const next = { ...this.uploadState };
      list.forEach(r => {
        const rid = r?.round_id || r?.id;
        if (!rid) return;
        if (r?.video_link) {
          const existing = next[rid] || {};
          if (existing.status === 'uploading') return;
          next[rid] = { ...existing, percent: 100, status: 'completed' };
        }
      });
      this.uploadState = next;
    },
    hasCompletedScoring(athlete) {
      return Boolean(athlete?.completed_scoring_at || athlete?.completedScoringAt);
    },
    roundTricklistSelectionValue(athlete) {
      if (!athlete?.tricklist_version_id) {
        return '';
      }

      return String(athlete.tricklist_version_id);
    },
    isRoundTricklistPending(athlete) {
      const roundId = athlete?.round_id || athlete?.id;
      return this.pendingRoundOverrideIds.has(roundId);
    },
    canEditRoundTricklist(athlete) {
      if (!athlete) {
        return false;
      }

      if (this.hasCompletedScoring(athlete) || athlete?.validated_at) {
        return false;
      }

      return ![6, 7, 8, 10, 11, 12, 19, 20, 22, 23].includes(Number(athlete?.status_id));
    },
    async updateRoundTricklistOverride(athlete, rawValue) {
      const roundId = athlete?.round_id || athlete?.id;
      if (!roundId) {
        return;
      }

      const tricklistVersionId = rawValue === '' ? null : Number(rawValue);
      this.pendingRoundOverrideIds = new Set([...this.pendingRoundOverrideIds, roundId]);

      try {
        const response = await this.$axios.patch(`/rounds/${roundId}/tricklist-version`, {
          tricklist_version_id: tricklistVersionId,
        }, {
          headers: this.authHeader,
        });

        const updatedRound = response.data?.round;
        if (!updatedRound) {
          return;
        }

        await this.$store.dispatch('round/upsertRound', updatedRound);
      } catch (error) {
        console.error('Error updating round tricklist override:', error);
      } finally {
        const nextPending = new Set(this.pendingRoundOverrideIds);
        nextPending.delete(roundId);
        this.pendingRoundOverrideIds = nextPending;
      }
    },
    isClickable(athlete) {
      const statusId = Number(athlete?.status_id);
      if (statusId === 11 || statusId === 12) {
        return this.hasCompletedScoring(athlete);
      }

      return [7, 10].includes(statusId);
    },
    getStatusLabel(status_id) {
      const labels = {
        13: this.$t('judge.pending'),
        6: this.$t('judge.scoring'),
        11: this.$t('judge.disqualified'),
        12: this.$t('judge.injured'),
        14: this.$t('judge.eliminated'),
        7: this.$t('judge.completed'),
        16: this.$t('judge.recording'),
        17: this.$t('judge.recorded'),
        10: this.$t('judge.published'),
        19: this.$t('judge.review'),
        20: this.$t('judge.status_20'),
        21: this.$t('judge.status_21'),
        22: this.$t('judge.status_22'),
        23: this.$t('judge.status_23') || this.$t('judge.no_submission'),
      };
      return labels[status_id] || '-';
    },
    statusClass(status_id) {
      return {
        'status-pending': status_id === 13,
        'status-scoring': status_id === 6,
        'status-disqualified': status_id === 11,
        'status-injured': status_id === 12,
        'status-completed': status_id === 7,
        'status-recording': status_id === 16,
        'status-recorded': status_id === 17,
        'status-published': status_id === 10,
        'status-review': status_id === 19 || status_id === 22 || status_id === 23,
        'status-uploading': status_id === 20,
        'status-status_21': status_id === 21,
      };
    },
    formatScore(score) {
      const value = parseFloat(score);
      return isNaN(value) ? '0.00' : value.toFixed(2);
    },
    needsAlert(athlete) {
      return Number(athlete?.status_id) === 7 && this.isInnerAthlete(this.user, athlete) && !athlete.validated_at;
    },
    isOwner(athlete) {
      return Number(athlete?.status_id) === 7 && (this.isOwnerOrganizer(this.user, this.competitionData) || this.isInnerAthlete(this.user, athlete));
    },
    isValid(athlete) {
      return athlete.validated_at;
    },
    isInjured(athlete) {
      return athlete?.injured_at;
    },
    isEliminated(athlete) {
      return athlete?.disqualified_at;
    },
    isQualificationContext() {
      return this.selectedStage === null && this.selectedRound !== null;
    },
    isBestQualificationContext() {
      return this.isQualificationContext() && this.selectedRound === 'best';
    },
    isSingleQualificationContext() {
      return this.isQualificationContext() && this.selectedRound !== 'best' && !this.currentCompetition?.is_qualification_dual;
    },
    isNonVsStageListContext() {
      return this.selectedStage !== null && !this.isVsCompetition;
    },
    isAdvancingContext() {
      return this.isSingleQualificationContext() || this.isBestQualificationContext() || this.isNonVsStageListContext();
    },
    isAdvancingContextResolved() {
      const resolvedStatuses = new Set([10, 11, 12, 14, 23]);
      const list = Array.isArray(this.rounds) ? this.rounds : [];
      return list.length > 0 && list.every(athlete => resolvedStatuses.has(Number(athlete?.status_id)));
    },
    resolveNextRoundSize() {
      const orderedRoundSizes = [...this.roundSizes]
        .filter(size => Number.isFinite(Number(size?.id)))
        .sort((left, right) => Number(right.id) - Number(left.id));

      if (!orderedRoundSizes.length) {
        return null;
      }

      if (this.isQualificationContext()) {
        return orderedRoundSizes[0] || null;
      }

      if (!this.isNonVsStageListContext()) {
        return null;
      }

      const currentStageId = Number(this.selectedStage);
      const currentStageIndex = orderedRoundSizes.findIndex(size => Number(size?.id) === currentStageId);
      if (currentStageIndex === -1) {
        return null;
      }

      return orderedRoundSizes[currentStageIndex + 1] || null;
    },
    resolveAdvancerCount() {
      const explicitCount = Number(this.advancingCount);
      if (Number.isFinite(explicitCount) && explicitCount > 0) {
        return explicitCount;
      }

      const nextRoundSize = this.resolveNextRoundSize();
      const athleteCount = Number(nextRoundSize?.athlete_count);
      return Number.isFinite(athleteCount) && athleteCount > 0 ? athleteCount : 0;
    },
    isAdvancingAthlete(athlete) {
      return this.advancingAthleteIds.has(athlete?.id) && Number(athlete?.status_id) === 10;
    },
    ensureValidPage() {
      if (this.currentPage > this.totalPages) {
        this.currentPage = this.totalPages;
      }

      if (this.currentPage < 1) {
        this.currentPage = 1;
      }
    },
    goToPreviousPage() {
      if (this.currentPage <= 1) return;
      this.currentPage -= 1;
    },
    goToNextPage() {
      if (this.currentPage >= this.totalPages) return;
      this.currentPage += 1;
    },
    toggleScoreSort() {
      this.scoreSortDirection = this.scoreSortDirection === 'desc' ? 'asc' : 'desc';
    },
    handleRoundClick(athlete) {
      if (!this.isClickable(athlete)) return;
      const payload = { athlete, selectedStage: this.selectedStage, selectedRound: this.selectedRound };
      const forceViewOnly = Number(athlete?.status_id) === 10;
      if (!forceViewOnly && this.needsAlert(athlete)) this.$emit('review-round', payload); else this.$emit('view-round', payload);
    },
    uploadButtonConfig(athlete) {
      const statusId = Number(athlete?.status_id);
      const hasPublished = !!athlete?.published_at;
      if (!athlete || hasPublished || [7, 19, 20, 21].includes(statusId)) {
        return { visible: false, expired: false };
      }
      if (statusId === 13) {
        return { visible: true, expired: false };
      }
      if (statusId === 23) {
        return { visible: !hasPublished, expired: true };
      }
      return { visible: false, expired: false };
    },
    canShowVideoActions(athlete) {
      const config = this.uploadButtonConfig(athlete);
      return this.isOnlineCompetition && this.isInnerAthlete(this.user, athlete) && config.visible;
    },
    getStageCode(athlete) {
      if (!athlete) return null;
      if (athlete.round_size_id) {
        const stage = this.roundSizesById[Number(athlete.round_size_id)] || null;
        if (stage?.stage_code) return stage.stage_code;
        if (stage?.athlete_count) return `round_${stage.athlete_count}`;
      }
      if (athlete.round_number) return `qualification_${athlete.round_number}`;
      return null;
    },
    roundLabel(athlete) {
      if (!athlete) return null;
      const stage = athlete.round_size_id ? this.roundSizesById[Number(athlete.round_size_id)] || null : null;
      const code = this.getStageCode(athlete) || stage?.stage_code || null;
      if (code && /^stage\d+$/i.test(code)) {
        const tKey = `rounds.${code.toLowerCase()}`;
        if (this.$te && this.$te(tKey)) return this.$t(tKey);
      }
      if (code && /^qualification_\d+$/i.test(code)) {
        const n = code.split('_')[1];
        const base = this.$t ? this.$t('rounds.qualifying') : 'Qualification';
        return n ? `${base} ${n}` : base;
      }
      if (stage?.name) return stage.name;
      if (code) return code;
      if (stage?.athlete_count) return `${this.$t('rounds.stage') || 'Etapa'} ${stage.athlete_count}`;
      if (athlete.round_number) return `${this.$t('rounds.round') || 'Ronda'} ${athlete.round_number}`;
      return null;
    },
    getCode(athlete) {
      if (!athlete) return null;
      return athlete.verification_code || null;
    },
    onUploadButtonClick(athlete) {
      const config = this.uploadButtonConfig(athlete);
      if (config.expired) {
        this.showExpiredModal = true;
        return;
      }
      this.handleUploadButton(athlete);
    },
    handleUploadButton(athlete) {
      if (!athlete) return;
      const state = this.uploadState[athlete.round_id];
      this.uploadModalAthlete = athlete;
      this.uploadModalFile = this.uploadFiles[athlete.round_id] || null;
      if (!state || state.status === 'completed') {
        this.uploadModalError = '';
        this.uploadModalIssues = [];
        this.uploadModalMeta = null;
      }
      this.showUploadModal = true;
    },
    closeUploadModal() {
      if (this.isModalUploading) return;
      this.showUploadModal = false;
      this.uploadModalAthlete = null;
      this.uploadModalFile = null;
      this.uploadModalError = '';
      this.uploadModalIssues = [];
      this.uploadModalMeta = null;
    },
    closeExpiredModal() {
      this.showExpiredModal = false;
    },
    async onUploadFileChange(file) {
      this.uploadModalFile = file || null;
      this.uploadModalError = '';
      this.uploadModalIssues = [];
      this.uploadModalMeta = null;
      if (!file) return;
      const { issues, videoWidth, videoHeight } = await this.validateVideoFile(file);
      this.uploadModalIssues = issues;
      this.uploadModalMeta = {
        width: videoWidth,
        height: videoHeight,
        sizeMb: Number((file.size / (1024 * 1024)).toFixed(1))
      };
      if (issues.length) {
        const messages = [];
        if (issues.includes('vertical')) messages.push(this.$t('competitions.messages.video_vertical') || 'Tu video está en vertical. Sube uno en horizontal.');
        if (issues.includes('low-res')) messages.push(this.$t('competitions.messages.video_low_res') || 'Resolución baja (mínimo recomendado 1280x1080).');
        if (issues.includes('too-big')) messages.push(this.$t('competitions.messages.video_too_big') || 'El archivo es muy pesado. Recórtalo o baja la resolución.');
        if (issues.includes('unreadable')) messages.push(this.$t('competitions.messages.video_unreadable') || 'No se pudo leer el archivo de video.');
        this.uploadModalError = messages.join(' ');
      }
    },
    async confirmUpload() {
      const athlete = this.uploadModalAthlete;
      if (!athlete) return;
      const roundId = athlete.round_id;
      if (!this.authToken) {
        this.uploadModalError = this.$t('competitions.messages.upload_missing_token') || 'Falta token de autenticacion';
        return;
      }
      if (!this.uploadModalFile) {
        this.uploadModalError = this.$t('competitions.messages.upload_missing_file') || 'Selecciona un archivo de video';
        return;
      }
      if (this.uploadModalIssues.length) {
        this.uploadModalError = this.uploadModalError || (this.$t('competitions.messages.video_blocked') || 'Corrige el video antes de subirlo.');
        return;
      }
      const file = this.uploadModalFile;
      this.uploadFiles = { ...this.uploadFiles, [roundId]: file };
      this.uploadModalError = '';
      const existingState = this.uploadState[roundId];
      const reuseUploadId = existingState?.uploadId || null;
      const reuseNext = Number.isInteger(existingState?.nextChunk) ? existingState.nextChunk : 0;
      await this.performChunkedUpload({ roundId, file, startChunk: reuseNext, uploadId: reuseUploadId });
    },
    async resumeUpload(roundId, athlete) {
      const file = this.uploadFiles[roundId];
      if (!file) {
        this.uploadModalAthlete = athlete;
        this.uploadModalFile = null;
        this.uploadModalError = '';
        this.showUploadModal = true;
        return;
      }
      const state = this.uploadState[roundId] || {};
      const startChunk = Number.isInteger(state.nextChunk) ? state.nextChunk : 0;
      await this.performChunkedUpload({ roundId, file, startChunk, uploadId: state.uploadId || null });
    },
    async retryUpload() {
      const athlete = this.uploadModalAthlete;
      const roundId = athlete?.round_id;
      if (!roundId) return;
      const file = this.uploadFiles[roundId];
      if (!file) {
        this.uploadModalError = this.$t('competitions.messages.upload_missing_file') || 'Selecciona un archivo de video';
        return;
      }
      const state = this.uploadState[roundId] || {};
      const startChunk = Number.isInteger(state.nextChunk) ? state.nextChunk : 0;
      this.uploadModalError = '';
      await this.performChunkedUpload({ roundId, file, startChunk, uploadId: state.uploadId || null });
    },
    async performChunkedUpload({ roundId, file, startChunk = 0, uploadId = null }) {
      const chunkSize = 10 * 1024 * 1024;
      const totalChunks = Math.max(1, Math.ceil(file.size / chunkSize));
      const effectiveUploadId = uploadId || (crypto?.randomUUID && crypto.randomUUID()) || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      this.uploadState = {
        ...this.uploadState,
        [roundId]: {
          percent: Math.round((startChunk / totalChunks) * 100),
          status: 'uploading',
          uploadId: effectiveUploadId,
          nextChunk: startChunk,
          totalChunks,
          originalName: file.name || 'video.mp4'
        }
      };
      const sendChunk = async (chunkIndex) => {
        const start = chunkIndex * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const blob = file.slice(start, end, file.type || 'video/mp4');
        const formData = new FormData();
        formData.append('upload_id', effectiveUploadId);
        formData.append('chunk_index', chunkIndex);
        formData.append('total_chunks', totalChunks);
        formData.append('original_name', file.name || 'video.mp4');
        formData.append('video', blob, file.name || 'video.mp4');
        await this.$axios.post(`/rounds/${roundId}/upload-chunk`, formData, { headers: { ...this.authHeader, 'Content-Type': 'multipart/form-data' } });
      };
      try {
        for (let i = startChunk; i < totalChunks; i += 1) {
          await sendChunk(i);
          const percent = Math.round(((i + 1) / totalChunks) * 100);
          this.uploadState = {
            ...this.uploadState,
            [roundId]: {
              ...this.uploadState[roundId],
              percent,
              status: 'uploading',
              nextChunk: i + 1,
            }
          };
        }
        await this.$axios.post(`/rounds/${roundId}/complete-upload`, {
          upload_id: effectiveUploadId,
          total_chunks: totalChunks,
          original_name: file.name || 'video.mp4'
        }, { headers: { ...this.authHeader } });
        this.uploadState = {
          ...this.uploadState,
          [roundId]: {
            ...this.uploadState[roundId],
            percent: 100,
            status: 'completed',
            nextChunk: totalChunks,
            uploadId: effectiveUploadId,
          }
        };
      } catch (e) {
        console.error('Error subiendo video', e);
        const serverMsg = e?.response?.data?.message || e?.message;
        this.uploadModalError = serverMsg || (this.$t('competitions.messages.upload_failed') || 'No se pudo subir el video');
        this.uploadState = {
          ...this.uploadState,
          [roundId]: {
            ...this.uploadState[roundId],
            status: 'error'
          }
        };
      }
    },
    validateVideoFile(file) {
      const MAX_SIZE_MB = 1024;
      const MIN_SHORT_SIDE = 1080;
      const MIN_LONG_SIDE = 1280;
      return new Promise((resolve) => {
        const video = document.createElement('video');
        const url = URL.createObjectURL(file);
        video.preload = 'metadata';
        const finalize = (issues, videoWidth = null, videoHeight = null) => {
          URL.revokeObjectURL(url);
          const sizeMb = file.size / (1024 * 1024);
          if (sizeMb > MAX_SIZE_MB) issues.push('too-big');
          resolve({ issues, videoWidth, videoHeight });
        };
        video.onerror = () => finalize(['unreadable']);
        video.onloadedmetadata = () => {
          const { videoWidth, videoHeight } = video;
          const issues = [];
          const longSide = Math.max(videoWidth, videoHeight);
          const shortSide = Math.min(videoWidth, videoHeight);
          if (videoHeight > videoWidth) issues.push('vertical');
          if (shortSide < MIN_SHORT_SIDE || longSide < MIN_LONG_SIDE) issues.push('low-res');
          finalize(issues, videoWidth, videoHeight);
        };
        video.src = url;
      });
    },
  }
};
</script>

<style scoped>
.ranking-table {
  width: 100%;
  margin-top: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  padding: 16px;
}

.table-wrapper {
  overflow-x: auto;
}

.table-pagination {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 12px;
}

.table-pagination-status {
  font-size: 0.9rem;
  font-weight: 600;
  color: #4b5563;
}

.ranking-table table {
  width: 100%;
  border-collapse: collapse;
}

.ranking-table th,
.ranking-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #ddd;
}

.ranking-table th {
  background: #007bff;
  color: white;
}

.athlete-header {
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 10px;
}
.athlete-header .number {
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}


.athlete-img {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: 2px solid #ddd;
  object-fit: cover;
}

.status-pending    { text-shadow: 0 0 4px gray;   color: #000; }
.status-scoring    { text-shadow: 0 0 4px blue;   color: #000; }
.status-disqualified { text-shadow: 0 0 4px red;   color: #000; }
.status-injured    { text-shadow: 0 0 4px orange; color: #000; }
.status-completed  { text-shadow: 0 0 4px green;  color: #000; }
.status-recording  { text-shadow: 0 0 6px #bd0f3f; color: #000; }
.status-recorded   { text-shadow: 0 0 6px #8db70d; color: #000; }
.status-published   { text-shadow: 0 0 6px #980db7; color: #000; }
.status-review    { text-shadow: 0 0 4px blue;   color: #000; }
.status-uploading { text-shadow: 0 0 6px #bd0f3f; color: #000; }
.status-status_21 { text-shadow: 0 0 6px #8db70d; color: #000; }


.highlighted-row {
  background-color: rgba(73, 133, 108, 0.15);
  transition: background-color 0.5s ease;
}
tr.highlighted-row {
  background-color: #ffffcc;
}

tr.qualifier-row {
  background-color: rgba(14, 124, 102, 0.12);
}

tr.qualifier-row td:first-child {
  box-shadow: inset 4px 0 0 rgba(14, 124, 102, 0.7);
}

tr.qualifier-row:hover {
  background-color: rgba(14, 124, 102, 0.18);
}

.clickable-row:hover {
  cursor: pointer;
  background-color: #f0f0f0;
}

.round-warning-col {
  width: 80px;
  max-width: 100px;
  text-align: center;
  padding: 4px;
}

.round-warning-col {
  width: 100px;
  max-width: 100px;
  text-align: center;
  overflow: hidden;
  position: relative;
}

.message-alert {
  display: inline-block;
  white-space: nowrap;
  animation: scroll-left 8s linear infinite;
  font-weight: bold;
  color: #856404;
  background-color: #fff3cd;
  border: 1px solid #ffeeba;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 0.75rem;
}

.message-check {
  display: inline-block;
  background: #e8f4ff;
  color: #007bff;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 0.75rem;
}

.check-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 2px 4px;
  border-radius: 4px;
}

.check-wrapper--single {
  background: #f1f5f9;
  color: #6b7280;
}

.check-wrapper--double {
  background: #e8f4ff;
  color: #0f172a;
}

.status-message {
  margin-top: 4px;
}

.code-reminder {
  font-size: 0.85rem;
}

.code-pill {
  display: inline-block;
  margin-left: 4px;
  padding: 2px 6px;
  background: #eef2ff;
  color: #3730a3;
  border-radius: 6px;
  font-family: monospace;
  border: 1px solid #c7d2fe;
}

.progress-circle {
  position: relative;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #e5e7eb;
  display: grid;
  place-items: center;
  color: #0f172a;
  font-size: 11px;
  font-weight: 700;
}

.progress-text {
  position: relative;
  z-index: 1;
}

.video-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 50%;
}

.video-button--blocked {
  opacity: 0.5;
  cursor: not-allowed;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
  padding: 12px;
}

.modal-card {
  background: white;
  padding: 16px;
  border-radius: 8px;
  width: 420px;
  max-width: 90vw;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
}

@media (max-width: 768px) {
  .ranking-table {
    padding: 12px;
  }

  .ranking-table table,
  .ranking-table thead,
  .ranking-table tbody,
  .ranking-table th,
  .ranking-table td,
  .ranking-table tr {
    display: block;
    width: 100%;
  }

  .ranking-table thead {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    margin: -1px;
    padding: 0;
    border: 0;
  }

  .ranking-table tr {
    margin-bottom: 12px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
    background: #fff;
  }

  .ranking-table td {
    display: flex;
    align-items: center;
    justify-content: space-between;
    text-align: right;
    border-bottom: 1px solid #f3f4f6;
    padding: 10px 12px;
  }

  .ranking-table td:last-child {
    border-bottom: none;
  }

  .ranking-table td::before {
    content: attr(data-label);
    font-weight: 600;
    text-transform: uppercase;
    font-size: 0.75rem;
    color: #6b7280;
    letter-spacing: 0.01em;
    text-align: left;
    margin-right: 12px;
    flex: 1 1 50%;
  }

  .ranking-table td[data-label=""]::before {
    content: '';
  }

  .ranking-table .athlete-header-cell {
    padding: 12px;
  }

  .ranking-table .athlete-header {
    justify-content: flex-start;
    gap: 12px;
  }

  .ranking-table .athlete-name {
    font-weight: 600;
    text-align: left;
  }

  .ranking-table .round-warning-col {
    display: flex;
    justify-content: flex-start;
  }

  .ranking-table .score {
    font-variant-numeric: tabular-nums;
  }

  .table-pagination {
    justify-content: space-between;
    gap: 8px;
  }

  .table-pagination-status {
    font-size: 0.85rem;
  }
}

@keyframes scroll-left {
  0% { transform: translateX(0); }
  100% { transform: translateX(-100%); }
}
</style>
